import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, Package, FolderOpen, ShoppingBag, ArrowLeft, Eye, EyeOff, Edit2, X, FileText, Trash2, Users, GripVertical, Shield, Bell } from 'lucide-react';
import InvoiceView from '../components/InvoiceView';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingUserCount, setPendingUserCount] = useState(0);
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [adminRole, setAdminRole] = useState('');  // owner, staff_admin, admin
  const navigate = useNavigate();
  const lastNotifRef = useRef({ users: 0, orders: 0 });
  const titleBlinkRef = useRef(null);
  const originalTitle = useRef('Krishna Machinery Stores');

  const [newCategory, setNewCategory] = useState({ name: '', image_url: '' });
  const [newProduct, setNewProduct] = useState({ name: '', category_id: '', price: 0, image_url: '', in_stock: true });
  
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const cleanName = (fn) => fn ? fn.replace(/\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i, '') : '';

  // Admin products pagination state
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [productHasMore, setProductHasMore] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const PRODUCTS_PER_PAGE = 50;

  // Product selection for bulk delete
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [productReorderMode, setProductReorderMode] = useState(false);

  useEffect(() => {
    if (authenticated && activeTab) {
      fetchData();
    }
  }, [authenticated, activeTab, productPage, productCategoryFilter]);

  // Fetch notification counts when admin logs in + polling every 30s
  const fetchNotifications = useCallback(async (isInitial = false) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/notifications`);
      const { pending_users, pending_orders } = res.data;
      setPendingUserCount(pending_users);
      setPendingOrderCount(pending_orders);

      // Send browser notification if counts increased (not on initial load)
      if (!isInitial) {
        const prev = lastNotifRef.current;
        const newUsers = pending_users > prev.users;
        const newOrders = pending_orders > prev.orders;
        if (newUsers || newOrders) {
          const parts = [];
          if (newUsers) parts.push(`${pending_users} pending user(s)`);
          if (newOrders) parts.push(`${pending_orders} new order(s)`);
          sendBrowserNotification('Krishna Machinery Stores', parts.join(' & '));
          startTitleBlink(parts.join(' & '));
        }
      }
      lastNotifRef.current = { users: pending_users, orders: pending_orders };
    } catch {}
  }, []);

  useEffect(() => {
    if (authenticated) {
      fetchNotifications(true);
      const interval = setInterval(() => fetchNotifications(false), 30000);
      return () => clearInterval(interval);
    }
  }, [authenticated, fetchNotifications]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Stop title blink when tab is focused
  useEffect(() => {
    const handleFocus = () => {
      if (titleBlinkRef.current) {
        clearInterval(titleBlinkRef.current);
        titleBlinkRef.current = null;
        document.title = originalTitle.current;
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const sendBrowserNotification = (title, body) => {
    if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
      new Notification(title, { body, icon: '/logo192.png', tag: 'kms-admin' });
    }
  };

  const startTitleBlink = (msg) => {
    if (document.hidden && !titleBlinkRef.current) {
      let show = true;
      titleBlinkRef.current = setInterval(() => {
        document.title = show ? `⚡ ${msg}` : originalTitle.current;
        show = !show;
      }, 1000);
    }
  };

  // Reset page when switching to products tab
  useEffect(() => {
    if (activeTab === 'products') {
      setProductPage(1);
    }
  }, [activeTab]);

  // Debounced search for products
  const handleProductSearch = (value) => {
    setProductSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setProductPage(1);
      // Trigger fetch via a state change
      setProductCategoryFilter(prev => prev); // no-op to not change filter
      // We need to fetch explicitly since the deps won't change
      fetchProductsDebounced(value);
    }, 400));
  };

  const fetchProductsDebounced = async (search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: PRODUCTS_PER_PAGE });
      if (search.trim()) params.append('search', search.trim());
      if (productCategoryFilter) params.append('category_id', productCategoryFilter);

      const res = await axios.get(`${BACKEND_URL}/api/admin/products?${params.toString()}`);
      setProducts(res.data.products);
      setProductTotal(res.data.total);
      setProductHasMore(res.data.has_more);
      setProductPage(1);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const [adminPhone, setAdminPhone] = useState('');
  const [loginError, setLoginError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, { password, phone: adminPhone });
      if (response.data.success) {
        setAdminRole(response.data.role || 'staff_admin');
        setAuthenticated(true);
      }
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Invalid credentials');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'categories') {
        const res = await axios.get(`${BACKEND_URL}/api/categories`);
        setCategories(res.data);
      } else if (activeTab === 'products') {
        const params = new URLSearchParams({
          page: productPage,
          limit: PRODUCTS_PER_PAGE,
        });
        if (productSearch.trim()) params.append('search', productSearch.trim());
        if (productCategoryFilter) params.append('category_id', productCategoryFilter);

        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/products?${params.toString()}`),
          axios.get(`${BACKEND_URL}/api/categories`)
        ]);
        setProducts(productsRes.data.products);
        setProductTotal(productsRes.data.total);
        setProductHasMore(productsRes.data.has_more);
        setCategories(categoriesRes.data);
      } else if (activeTab === 'orders') {
        const res = await axios.get(`${BACKEND_URL}/api/orders`);
        setOrders(res.data);
      } else if (activeTab === 'users' || activeTab === 'super_user' || activeTab === 'category_access') {
        const res = await axios.get(`${BACKEND_URL}/api/admin/users`);
        setUsers(res.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/categories`, newCategory);
      setCategories([...categories, response.data]);
      setNewCategory({ name: '', image_url: '' });
    } catch (error) {
      alert('Error adding category');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory.image_url && !editingCategory.name) {
      alert('Please provide category name');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.put(`${BACKEND_URL}/api/categories/${editingCategory.id}`, {
        name: editingCategory.name,
        image_url: editingCategory.image_url
      });
      
      // Update local state instead of refetching all data
      setCategories(categories.map(cat => 
        cat.id === editingCategory.id ? response.data : cat
      ));
      setEditingCategory(null);
    } catch (error) {
      alert('Error updating category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Are you sure? This will delete all products in this category.')) {
      setLoading(true);
      try {
        await axios.delete(`${BACKEND_URL}/api/categories/${id}`);
        setCategories(categories.filter(cat => cat.id !== id));
        if (activeTab === 'products') {
          setProducts(products.filter(prod => prod.category_id !== id));
        }
      } catch (error) {
        alert('Error deleting category');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/products`, newProduct);
      setNewProduct({ name: '', category_id: newProduct.category_id, price: 0, image_url: '', in_stock: true });
      // Refresh current page to show updated list
      fetchData();
    } catch (error) {
      alert('Error adding product');
      setLoading(false);
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct.name || editingProduct.price === undefined) {
      alert('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const updateData = {
        name: editingProduct.name,
        price: parseFloat(editingProduct.price),
        in_stock: editingProduct.in_stock
      };
      
      // Only include image_url if it exists
      if (editingProduct.image_url) {
        updateData.image_url = editingProduct.image_url;
      }
      
      const response = await axios.put(`${BACKEND_URL}/api/products/${editingProduct.id}`, updateData);
      
      // Update local state instead of refetching all data
      setProducts(products.map(prod => 
        prod.id === editingProduct.id ? response.data : prod
      ));
      setEditingProduct(null);
    } catch (error) {
      console.error('Product update error:', error);
      alert('Error updating product');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure?')) {
      setLoading(true);
      try {
        await axios.delete(`${BACKEND_URL}/api/products/${id}`);
        // Refresh current page
        fetchData();
      } catch (error) {
        alert('Error deleting product');
        setLoading(false);
      }
    }
  };

  const handleImageUpload = async (e, type, isEditing = false) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadedFileName(file.name);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = response.data.url;
      
      if (type === 'category') {
        if (isEditing) {
          setEditingCategory(prev => ({ ...prev, image_url: imageUrl }));
        } else {
          setNewCategory(prev => ({ ...prev, image_url: imageUrl }));
        }
      } else {
        if (isEditing) {
          setEditingProduct(prev => ({ ...prev, image_url: imageUrl }));
        } else {
          setNewProduct(prev => ({ ...prev, image_url: imageUrl }));
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image. Please try again.');
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md" data-testid="admin-login">
          <div className="flex items-center justify-center mb-6">
            <Lock className="h-12 w-12 text-[#1A3626]" />
          </div>
          <h2 className="text-2xl font-bold text-[#1A3626] text-center mb-6">Admin Login</h2>
          {loginError && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-lg mb-4">{loginError}</div>}
          <form onSubmit={handleLogin} className="space-y-3">
            <div>
              <input
                type="tel"
                placeholder="Phone number (Staff Admin)"
                maxLength={10}
                value={adminPhone}
                onChange={(e) => setAdminPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3 text-base"
                data-testid="admin-phone-input"
              />
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3 pr-12 text-base"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-500 hover:text-[#1A3626]"
                data-testid="toggle-password"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <button
              type="submit"
              className="w-full bg-[#C05A3E] text-white hover:bg-[#A64B31] rounded-md px-6 py-3 font-semibold transition-colors"
              data-testid="admin-login-button"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#1A3626] hover:text-[#C05A3E] mb-6 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-medium">Back to Home</span>
        </button>

        <h1 className="text-3xl sm:text-4xl font-bold text-[#1A3626] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-title">
          Admin Panel
        </h1>

        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('categories')}
            className={`flex items-center gap-2 px-6 py-3 rounded-md font-medium transition-colors ${
              activeTab === 'categories'
                ? 'bg-[#1A3626] text-white'
                : 'bg-white text-[#1A3626] hover:bg-gray-100'
            }`}
            data-testid="categories-tab"
          >
            <FolderOpen className="h-5 w-5" />
            Categories
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`flex items-center gap-2 px-6 py-3 rounded-md font-medium transition-colors ${
              activeTab === 'products'
                ? 'bg-[#1A3626] text-white'
                : 'bg-white text-[#1A3626] hover:bg-gray-100'
            }`}
            data-testid="products-tab"
          >
            <Package className="h-5 w-5" />
            Products
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`flex items-center gap-2 px-6 py-3 rounded-md font-medium transition-colors relative ${
              activeTab === 'orders'
                ? 'bg-[#1A3626] text-white'
                : 'bg-white text-[#1A3626] hover:bg-gray-100'
            }`}
            data-testid="orders-tab"
          >
            <ShoppingBag className="h-5 w-5" />
            Orders
            {pendingOrderCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#C05A3E] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center" data-testid="pending-orders-badge">
                {pendingOrderCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-6 py-3 rounded-md font-medium transition-colors relative ${
              activeTab === 'users'
                ? 'bg-[#1A3626] text-white'
                : 'bg-white text-[#1A3626] hover:bg-gray-100'
            }`}
            data-testid="users-tab"
          >
            <Users className="h-5 w-5" />
            Users
            {pendingUserCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#C05A3E] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center" data-testid="pending-badge">
                {pendingUserCount}
              </span>
            )}
          </button>
          {adminRole === 'owner' && (
            <button
              onClick={() => setActiveTab('super_user')}
              className={`flex items-center gap-2 px-6 py-3 rounded-md font-medium transition-colors ${
                activeTab === 'super_user'
                  ? 'bg-purple-700 text-white'
                  : 'bg-white text-purple-700 hover:bg-purple-50'
              }`}
              data-testid="super-user-tab"
            >
              <Shield className="h-5 w-5" />
              Super User
            </button>
          )}
          {adminRole === 'owner' && (
            <button
              onClick={() => setActiveTab('category_access')}
              className={`flex items-center gap-2 px-6 py-3 rounded-md font-medium transition-colors ${
                activeTab === 'category_access'
                  ? 'bg-[#1A3626] text-white'
                  : 'bg-white text-[#1A3626] hover:bg-gray-100'
              }`}
              data-testid="category-access-tab"
            >
              <Eye className="h-5 w-5" />
              Category Access
            </button>
          )}
        </div>

        {activeTab === 'categories' && (
          <div>
            <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
              <h2 className="text-xl font-semibold text-[#1A3626] mb-4">Add New Category</h2>
              <form onSubmit={handleAddCategory} className="space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Category Name"
                    required
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                    className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3"
                    data-testid="category-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'category')}
                    className="w-full"
                    data-testid="category-image-input"
                  />
                  {uploadedFileName && !newCategory.image_url && <p className="text-xs text-gray-500 mt-1 select-all">{cleanName(uploadedFileName)}</p>}
                  {newCategory.image_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={newCategory.image_url} alt="Preview" className="h-16 w-16 object-cover rounded" />
                      <p className="text-xs text-gray-500 select-all cursor-text">{cleanName(uploadedFileName)}</p>
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="bg-[#C05A3E] text-white hover:bg-[#A64B31] rounded-md px-6 py-3 font-semibold transition-colors"
                  data-testid="add-category-button"
                >
                  Add Category
                </button>
              </form>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-[#1A3626] mb-2">All Categories ({categories.length})</h2>
              <p className="text-xs text-gray-400 mb-4">Drag to reorder categories</p>
              <DndCategoryList categories={categories} setCategories={setCategories} editingCategory={editingCategory} setEditingCategory={setEditingCategory} handleUpdateCategory={handleUpdateCategory} handleDeleteCategory={handleDeleteCategory} handleImageUpload={handleImageUpload} />
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div>
            <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
              <h2 className="text-xl font-semibold text-[#1A3626] mb-4">Add New Product</h2>
              <form onSubmit={handleAddProduct} className="space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Product Name"
                    required
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3"
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <select
                    required
                    value={newProduct.category_id}
                    onChange={(e) => setNewProduct({ ...newProduct, category_id: e.target.value })}
                    className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3"
                    data-testid="product-category-select"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Price"
                    required
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({ ...newProduct, price: parseFloat(e.target.value) })}
                    className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-3"
                    data-testid="product-price-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'product')}
                    className="w-full"
                    data-testid="product-image-input"
                  />
                  {uploadedFileName && !newProduct.image_url && <p className="text-xs text-gray-500 mt-1 select-all">{cleanName(uploadedFileName)}</p>}
                  {newProduct.image_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={newProduct.image_url} alt="Preview" className="h-16 w-16 object-cover rounded" />
                      <p className="text-xs text-gray-500 select-all cursor-text">{cleanName(uploadedFileName)}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newProduct.in_stock}
                      onChange={(e) => setNewProduct({ ...newProduct, in_stock: e.target.checked })}
                      className="w-4 h-4 text-[#1A3626] border-gray-300 rounded focus:ring-[#1A3626]"
                      data-testid="product-stock-checkbox"
                    />
                    <span className="text-sm font-medium text-gray-700">In Stock</span>
                  </label>
                </div>
                <button
                  type="submit"
                  className="bg-[#C05A3E] text-white hover:bg-[#A64B31] rounded-md px-6 py-3 font-semibold transition-colors"
                  data-testid="add-product-button"
                >
                  Add Product
                </button>
              </form>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <h2 className="text-xl font-semibold text-[#1A3626]">All Products ({productTotal})</h2>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={productSearch}
                    onChange={(e) => handleProductSearch(e.target.value)}
                    className="bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md px-3 py-2 text-sm"
                    data-testid="admin-product-search"
                  />
                  <select
                    value={productCategoryFilter}
                    onChange={(e) => { setProductCategoryFilter(e.target.value); setProductPage(1); setSelectedProductIds(new Set()); }}
                    className="bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md px-3 py-2 text-sm"
                    data-testid="admin-product-category-filter"
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Bulk actions bar */}
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={products.length > 0 && selectedProductIds.size === products.length}
                    onChange={() => {
                      if (selectedProductIds.size === products.length) setSelectedProductIds(new Set());
                      else setSelectedProductIds(new Set(products.map(p => p.id)));
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-[#1A3626] focus:ring-[#1A3626]"
                    data-testid="select-all-products"
                  />
                  Select All
                </label>
                {selectedProductIds.size > 0 && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete ${selectedProductIds.size} selected product(s)?`)) return;
                      try {
                        await axios.post(`${BACKEND_URL}/api/products/bulk-delete`, Array.from(selectedProductIds));
                        setSelectedProductIds(new Set());
                        fetchData();
                      } catch { alert('Error deleting products'); }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-xs font-medium"
                    data-testid="bulk-delete-products-btn"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete Selected ({selectedProductIds.size})
                  </button>
                )}
                {productCategoryFilter && (
                  <button
                    onClick={async () => {
                      const catName = categories.find(c => c.id === productCategoryFilter)?.name || 'this category';
                      if (!window.confirm(`Delete ALL products in "${catName}"? This cannot be undone.`)) return;
                      try {
                        await axios.post(`${BACKEND_URL}/api/admin/products/delete-by-category`, { category_id: productCategoryFilter });
                        setSelectedProductIds(new Set());
                        fetchData();
                      } catch { alert('Error deleting products'); }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-700 text-white rounded-md hover:bg-red-800 transition-colors text-xs font-medium"
                    data-testid="delete-all-category-products-btn"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete All in Category
                  </button>
                )}
              </div>

              {/* Product reorder mode toggle - only when a category is filtered */}
              {productCategoryFilter && !productSearch.trim() && (
                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-200">
                  <button
                    onClick={() => setProductReorderMode(prev => !prev)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      productReorderMode ? 'bg-[#1A3626] text-white' : 'bg-gray-100 text-[#1A3626] hover:bg-gray-200'
                    }`}
                    data-testid="product-reorder-toggle"
                  >
                    <GripVertical className="h-3.5 w-3.5" />
                    {productReorderMode ? 'Exit Reorder' : 'Drag to Reorder'}
                  </button>
                  {productReorderMode && <p className="text-xs text-gray-400">Drag products to set display order</p>}
                </div>
              )}

              {productReorderMode && productCategoryFilter ? (
                <DndProductList products={products} setProducts={setProducts} />
              ) : loading ? (
                <div className="text-center py-8 text-gray-500">Loading products...</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm" data-testid="products-table">
                      <thead className="bg-gray-100 font-semibold text-[#1A3626]">
                        <tr>
                          <th className="p-3 w-8"></th>
                          <th className="p-3">Name</th>
                          <th className="p-3">Category</th>
                          <th className="p-3">Price</th>
                          <th className="p-3">Stock</th>
                          <th className="p-3">Image</th>
                          <th className="p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map(prod => {
                          const category = categories.find(c => c.id === prod.category_id);
                          const isEditing = editingProduct?.id === prod.id;
                          return (
                            <tr key={prod.id} className={`border-b border-gray-200 ${selectedProductIds.has(prod.id) ? 'bg-red-50/40' : ''}`}>
                              <td className="p-3">
                                <input type="checkbox" checked={selectedProductIds.has(prod.id)}
                                  onChange={() => { const n = new Set(selectedProductIds); n.has(prod.id) ? n.delete(prod.id) : n.add(prod.id); setSelectedProductIds(n); }}
                                  className="w-4 h-4 rounded border-gray-300 text-[#1A3626] focus:ring-[#1A3626]" />
                              </td>
                              <td className="p-3">
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={editingProduct.name}
                                    onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                                    className="border rounded px-2 py-1 w-full"
                                  />
                                ) : (
                                  prod.name
                                )}
                              </td>
                              <td className="p-3">{category?.name || 'N/A'}</td>
                              <td className="p-3">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editingProduct.price}
                                    onChange={(e) => setEditingProduct({ ...editingProduct, price: parseFloat(e.target.value) })}
                                    className="border rounded px-2 py-1 w-20"
                                  />
                                ) : (
                                  `₹${prod.price.toFixed(2)}`
                                )}
                              </td>
                              <td className="p-3">
                                {isEditing ? (
                                  <label className="flex items-center gap-1">
                                    <input
                                      type="checkbox"
                                      checked={editingProduct.in_stock}
                                      onChange={(e) => setEditingProduct({ ...editingProduct, in_stock: e.target.checked })}
                                      className="w-4 h-4"
                                    />
                                    <span className="text-xs">In Stock</span>
                                  </label>
                                ) : (
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${prod.in_stock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {prod.in_stock ? 'In Stock' : 'Out of Stock'}
                                  </span>
                                )}
                              </td>
                              <td className="p-3">
                                {isEditing ? (
                                  <div>
                                    <div className="mb-2">
                                      <img 
                                        src={editingProduct.image_url || prod.image_url} 
                                        alt={prod.name} 
                                        className="h-12 w-12 object-cover rounded border-2 border-blue-500" 
                                      />
                                    </div>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => handleImageUpload(e, 'product', true)}
                                      className="text-xs"
                                    />
                                    {editingProduct.image_url && editingProduct.image_url !== prod.image_url && (
                                      <p className="text-xs text-green-600 font-bold mt-1">Image ready</p>
                                    )}
                                  </div>
                                ) : (
                                  <img src={prod.image_url} alt={prod.name} className="h-12 w-12 object-cover rounded" />
                                )}
                              </td>
                              <td className="p-3">
                                <div className="flex gap-2">
                                  {isEditing ? (
                                    <>
                                      <button
                                        onClick={handleUpdateProduct}
                                        className="text-green-600 hover:text-green-800 font-medium text-xs"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={() => setEditingProduct(null)}
                                        className="text-gray-600 hover:text-gray-800 font-medium text-xs"
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => setEditingProduct(prod)}
                                        className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 text-xs"
                                      >
                                        <Edit2 className="h-3 w-3" /> Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeleteProduct(prod.id)}
                                        className="text-red-600 hover:text-red-800 font-medium text-xs"
                                        data-testid={`delete-product-${prod.id}`}
                                      >
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      Showing {((productPage - 1) * PRODUCTS_PER_PAGE) + 1}-{Math.min(productPage * PRODUCTS_PER_PAGE, productTotal)} of {productTotal}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setProductPage(p => Math.max(1, p - 1))}
                        disabled={productPage === 1}
                        className="px-4 py-2 text-sm rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-100 transition-colors"
                        data-testid="admin-products-prev"
                      >
                        Previous
                      </button>
                      <span className="px-4 py-2 text-sm text-[#1A3626] font-medium">
                        Page {productPage} of {Math.max(1, Math.ceil(productTotal / PRODUCTS_PER_PAGE))}
                      </span>
                      <button
                        onClick={() => setProductPage(p => p + 1)}
                        disabled={!productHasMore}
                        className="px-4 py-2 text-sm rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-100 transition-colors"
                        data-testid="admin-products-next"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <AdminOrdersSection orders={orders} fetchData={fetchData} />
        )}

        {activeTab === 'users' && (
          <AdminUsersSection users={users} fetchData={fetchData} setPendingUserCount={setPendingUserCount} adminRole={adminRole} />
        )}

        {activeTab === 'super_user' && adminRole === 'owner' && (
          <SuperUserManagement users={users} fetchData={fetchData} />
        )}

        {activeTab === 'category_access' && adminRole === 'owner' && (
          <CategoryAccessControl users={users} categories={categories} />
        )}
      </div>
    </div>
  );
};


// Drag-and-drop category list
const SortableCategoryItem = ({ cat, editingCategory, setEditingCategory, handleUpdateCategory, handleDeleteCategory, handleImageUpload }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: cat.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const isEditing = editingCategory?.id === cat.id;

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg mb-2">
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none">
        <GripVertical className="h-5 w-5" />
      </div>
      <img src={isEditing ? (editingCategory.image_url || cat.image_url) : cat.image_url} alt={cat.name} className="h-10 w-10 object-contain rounded flex-shrink-0 bg-gray-50" />
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="flex flex-col gap-2">
            <input type="text" value={editingCategory.name} onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })} className="border rounded px-2 py-1 text-sm" />
            <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'category', true)} className="text-xs" />
          </div>
        ) : (
          <p className="text-sm font-medium text-[#1A3626] truncate">{cat.name}</p>
        )}
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        {isEditing ? (
          <>
            <button onClick={handleUpdateCategory} className="text-green-600 text-xs font-medium">Save</button>
            <button onClick={() => setEditingCategory(null)} className="text-gray-500 text-xs font-medium">Cancel</button>
          </>
        ) : (
          <>
            <button onClick={() => setEditingCategory(cat)} className="text-blue-600 text-xs font-medium"><Edit2 className="h-3.5 w-3.5" /></button>
            <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-500 text-xs font-medium"><Trash2 className="h-3.5 w-3.5" /></button>
          </>
        )}
      </div>
    </div>
  );
};

const DndCategoryList = ({ categories, setCategories, editingCategory, setEditingCategory, handleUpdateCategory, handleDeleteCategory, handleImageUpload }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = categories.findIndex(c => c.id === active.id);
    const newIdx = categories.findIndex(c => c.id === over.id);
    const newOrder = arrayMove(categories, oldIdx, newIdx);
    setCategories(newOrder);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/categories/reorder`, newOrder.map(c => c.id));
    } catch { alert('Error saving order'); }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
        {categories.map(cat => (
          <SortableCategoryItem key={cat.id} cat={cat} editingCategory={editingCategory} setEditingCategory={setEditingCategory} handleUpdateCategory={handleUpdateCategory} handleDeleteCategory={handleDeleteCategory} handleImageUpload={handleImageUpload} />
        ))}
      </SortableContext>
    </DndContext>
  );
};

// Drag-and-drop product list
const SortableProductItem = ({ product }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: product.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg mb-2" data-testid={`sortable-product-${product.id}`}>
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none">
        <GripVertical className="h-5 w-5" />
      </div>
      {product.image_url && <img src={product.image_url} alt={product.name} className="h-10 w-10 object-cover rounded flex-shrink-0 bg-gray-50" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#1A3626] truncate">{product.name}</p>
      </div>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${product.in_stock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {product.in_stock ? 'In Stock' : 'Out'}
      </span>
    </div>
  );
};

const DndProductList = ({ products, setProducts }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = products.findIndex(p => p.id === active.id);
    const newIdx = products.findIndex(p => p.id === over.id);
    const newOrder = arrayMove(products, oldIdx, newIdx);
    setProducts(newOrder);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/products/reorder`, { order: newOrder.map(p => p.id) });
    } catch { alert('Error saving product order'); }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={products.map(p => p.id)} strategy={verticalListSortingStrategy}>
        {products.map(product => (
          <SortableProductItem key={product.id} product={product} />
        ))}
      </SortableContext>
    </DndContext>
  );
};

const AdminOrdersSection = ({ orders, fetchData }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === orders.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(orders.map(o => o.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} order(s)? This cannot be undone.`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/orders/bulk-delete`, Array.from(selectedIds));
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      alert('Error deleting orders');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/orders/${orderId}/status`, { status: newStatus });
      fetchData();
    } catch (error) {
      alert('Error updating status');
    }
  };

  const handleDeleteOrder = async (orderId, invoiceNum) => {
    if (window.confirm(`Delete order ${invoiceNum || orderId}? This action cannot be undone.`)) {
      try {
        await axios.delete(`${BACKEND_URL}/api/orders/${orderId}`);
        fetchData();
      } catch (error) {
        alert('Error deleting order');
      }
    }
  };

  const statusColor = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    delivered: 'bg-green-100 text-green-800',
  };

  const allSelected = orders.length > 0 && selectedIds.size === orders.length;

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-[#1A3626]">All Orders ({orders.length})</h2>
        {orders.length > 0 && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-gray-300 text-[#1A3626] focus:ring-[#1A3626]"
                data-testid="select-all-orders"
              />
              Select All
            </label>
            {selectedIds.size > 0 && (
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-xs font-medium"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete ({selectedIds.size})
              </button>
            )}
          </div>
        )}
      </div>
      <div className="space-y-3">
        {orders.map((order, index) => (
          <div key={order.id} className={`border rounded-lg p-4 transition-colors ${selectedIds.has(order.id) ? 'border-red-300 bg-red-50/30' : 'border-gray-200 hover:border-gray-300'}`} data-testid={`order-${index}`}>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selectedIds.has(order.id)}
                onChange={() => toggleSelect(order.id)}
                className="w-4 h-4 mt-1 rounded border-gray-300 text-[#1A3626] focus:ring-[#1A3626] cursor-pointer flex-shrink-0"
                data-testid={`select-order-${index}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-[#1A3626] text-sm">{order.invoice_number || 'N/A'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{order.order_date_ist} {order.order_time_ist}</p>
                  </div>
                  <select
                    value={order.status || 'pending'}
                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                    className={`text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${statusColor[order.status] || 'bg-gray-100 text-gray-800'}`}
                    data-testid={`order-status-${index}`}
                  >
                    <option value="pending">PENDING</option>
                    <option value="confirmed">CONFIRMED</option>
                    <option value="delivered">DELIVERED</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-sm">
                  <div><span className="text-gray-400">Customer:</span> <span className="font-medium">{order.customer_name}</span></div>
                  <div><span className="text-gray-400">Phone:</span> <span className="font-medium">{order.phone}</span></div>
                  {order.email && <div><span className="text-gray-400">Email:</span> <span className="font-medium">{order.email}</span></div>}
                  <div className="sm:col-span-2"><span className="text-gray-400">Address:</span> <span className="font-medium">{order.address}</span></div>
                </div>
                <div className="mb-3">
                  <p className="text-xs text-gray-400 mb-1">Items ({order.total_items} qty):</p>
                  <div className="bg-gray-50 rounded p-2">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm mb-0.5">
                        <span>{item.product_name} ({item.unit})</span>
                        <span className="font-medium">x {item.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-4 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="flex items-center gap-1.5 text-xs font-medium text-[#C05A3E] hover:text-[#A64B31] transition-colors"
                    data-testid={`view-invoice-${index}`}
                  >
                    <FileText className="h-3.5 w-3.5" />
                    View Invoice
                  </button>
                  <button
                    onClick={() => handleDeleteOrder(order.id, order.invoice_number)}
                    className="flex items-center gap-1.5 text-xs font-medium text-red-400 hover:text-red-600 transition-colors"
                    data-testid={`delete-order-${index}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedOrder && (
        <InvoiceView
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          isAdmin={true}
        />
      )}
    </div>
  );
};

const AdminUsersSection = ({ users, fetchData, setPendingUserCount, adminRole }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState(users);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/users/search?q=${encodeURIComponent(searchQuery)}`);
        setFilteredUsers(res.data);
      } catch { setFilteredUsers(users); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, users]);

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${userId}/status`, { status: newStatus });
      fetchData();
      const res = await axios.get(`${BACKEND_URL}/api/admin/users/pending-count`);
      setPendingUserCount(res.data.count);
    } catch { alert('Error updating user status'); }
  };

  const handleDeleteUser = async (userId, name) => {
    if (window.confirm(`Delete user "${name}"? This cannot be undone.`)) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`);
        fetchData();
        const res = await axios.get(`${BACKEND_URL}/api/admin/users/pending-count`);
        setPendingUserCount(res.data.count);
      } catch { alert('Error deleting user'); }
    }
  };

  const statusStyle = { pending: 'bg-yellow-100 text-yellow-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800' };
  const pending = filteredUsers.filter(u => u.status === 'pending');
  const others = filteredUsers.filter(u => u.status !== 'pending');
  const sorted = [...pending, ...others];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-xl font-semibold text-[#1A3626]">
          Users ({users.length})
          {pending.length > 0 && <span className="text-sm font-normal text-yellow-700 ml-2">({pending.length} pending)</span>}
        </h2>
        <input type="text" placeholder="Search shop, place, or phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md px-3 py-2 text-sm w-full sm:w-64" data-testid="user-search" />
      </div>
      <div className="space-y-2">
        {sorted.map((user) => (
          <div key={user.id} className={`p-3 rounded-lg border ${user.status === 'pending' ? 'border-yellow-200 bg-yellow-50/40' : 'border-gray-100'}`} data-testid={`user-${user.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <p className="font-medium text-sm text-[#1A3626]">{user.shop_name || user.first_name} {user.shop_place || user.last_name}</p>
                {user.email && <p className="text-xs text-gray-500 break-all">{user.email}</p>}
                {user.phone && <p className="text-xs text-gray-500">{user.phone}</p>}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {user.role === 'owner' && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-600 text-white">OWNER SUPER USER</span>}
                {user.role === 'staff_admin' && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-600 text-white">STAFF ADMIN</span>}
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusStyle[user.status] || 'bg-gray-100 text-gray-600'}`}>
                  {user.status}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {user.role !== 'owner' && user.status === 'pending' && (
                <>
                  <button onClick={() => handleStatusChange(user.id, 'approved')} className="px-2.5 py-1 text-[11px] font-semibold bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors" data-testid={`approve-${user.id}`}>Approve</button>
                  <button onClick={() => handleStatusChange(user.id, 'rejected')} className="px-2.5 py-1 text-[11px] font-semibold bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors" data-testid={`reject-${user.id}`}>Reject</button>
                </>
              )}
              {user.role !== 'owner' && user.status === 'approved' && user.role !== 'admin' && (
                <button onClick={() => handleStatusChange(user.id, 'rejected')} className="px-2.5 py-1 text-[11px] font-semibold text-red-500 border border-red-200 rounded-md hover:bg-red-50 transition-colors" data-testid={`revoke-${user.id}`}>Revoke</button>
              )}
              {user.role !== 'owner' && user.status === 'rejected' && (
                <button onClick={() => handleStatusChange(user.id, 'approved')} className="px-2.5 py-1 text-[11px] font-semibold text-green-600 border border-green-200 rounded-md hover:bg-green-50 transition-colors" data-testid={`reapprove-${user.id}`}>Approve</button>
              )}
              {user.role !== 'owner' && user.role !== 'admin' && (
                <button onClick={() => handleDeleteUser(user.id, `${user.shop_name || user.first_name}`)} className="px-2 py-1 text-[11px] font-semibold text-gray-400 hover:text-red-500 transition-colors" data-testid={`delete-user-${user.id}`} title="Delete user"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
          </div>
        ))}
        {filteredUsers.length === 0 && <p className="text-center text-gray-400 py-8 text-sm">{searchQuery ? 'No users match your search.' : 'No registered users yet.'}</p>}
      </div>
    </div>
  );
};

const SuperUserManagement = ({ users, fetchData }) => {
  const approvedUsers = users.filter(u => u.status === 'approved' && u.role !== 'owner');

  const handleRoleChange = async (userId, newRole) => {
    const label = newRole === 'staff_admin' ? 'Staff Admin' : 'Customer';
    if (!window.confirm(`Change this user's role to ${label}?`)) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${userId}/role`, { role: newRole });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating role');
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <h2 className="text-xl font-semibold text-purple-700 mb-2">Super User Management</h2>
      <p className="text-xs text-gray-400 mb-4">Assign or remove Staff Admin role from approved users.</p>

      <div className="mb-6 p-3 bg-purple-50 border border-purple-200 rounded-lg">
        <p className="text-xs font-semibold text-purple-700 mb-2">Owner Super Users (Protected)</p>
        {users.filter(u => u.role === 'owner').map(u => (
          <div key={u.id} className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-600 text-white">OWNER SUPER USER</span>
            <span className="text-sm">{u.shop_name || u.first_name} — {u.phone}</span>
          </div>
        ))}
      </div>

      <h3 className="text-sm font-semibold text-[#1A3626] mb-3">Manage Staff Roles</h3>
      <div className="space-y-2">
        {approvedUsers.map(user => (
          <div key={user.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm text-[#1A3626]">{user.shop_name || user.first_name} {user.shop_place || user.last_name}</p>
              <p className="text-xs text-gray-500">{user.phone} {user.email ? `| ${user.email}` : ''}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {user.role === 'staff_admin' ? (
                <>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-600 text-white">STAFF ADMIN</span>
                  <button onClick={() => handleRoleChange(user.id, 'user')} className="px-2.5 py-1 text-[11px] font-semibold text-red-500 border border-red-200 rounded-md hover:bg-red-50">Remove</button>
                </>
              ) : user.phone === '9607515786' ? (
                <button onClick={() => handleRoleChange(user.id, 'staff_admin')} className="px-2.5 py-1 text-[11px] font-semibold bg-blue-600 text-white rounded-md hover:bg-blue-700">Make Staff Admin</button>
              ) : (
                <span className="text-[10px] text-gray-400">Customer</span>
              )}
            </div>
          </div>
        ))}
        {approvedUsers.length === 0 && <p className="text-center text-gray-400 py-6 text-sm">No approved users to manage.</p>}
      </div>
    </div>
  );
};

const CategoryAccessControl = ({ users, categories }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [blockedCats, setBlockedCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const approvedUsers = users.filter(u => u.status === 'approved' && u.role !== 'owner');
  const [customerSearch, setCustomerSearch] = useState('');
const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const loadAccess = async (userId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/category-access/${userId}`);
      setBlockedCats(res.data.blocked_categories || []);
    } catch { setBlockedCats([]); }
  };

  const toggleCategory = (catId) => {
    setBlockedCats(prev => prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]);
  };

  const saveAccess = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/category-access`, { user_id: selectedUser.id, blocked_categories: blockedCats });
      alert('Category access updated!');
    } catch { alert('Error saving'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <h2 className="text-xl font-semibold text-[#1A3626] mb-2">Category Access Control</h2>
      <p className="text-xs text-gray-400 mb-4">Block or unblock categories for individual customers.</p>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Customer</label>
<div className="relative">
  <input
    type="text"
    placeholder="Type customer name or phone..."
    value={customerSearch}
    onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
    onFocus={() => setShowCustomerDropdown(true)}
    className="w-full bg-white border-2 border-gray-300 focus:border-[#1A3626] focus:ring-0 rounded-md p-2.5 text-sm"
    data-testid="category-access-user-select"
  />
  {showCustomerDropdown && (
    <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
      {approvedUsers
        .filter(u => `${u.shop_name || u.first_name || ''} ${u.shop_place || u.last_name || ''} ${u.phone || ''}`
          .toLowerCase().includes(customerSearch.toLowerCase()))
        .map(u => (
          <div
            key={u.id}
            onClick={() => {
              setSelectedUser(u);
              setCustomerSearch(`${u.shop_name || u.first_name} ${u.shop_place || u.last_name}`);
              setShowCustomerDropdown(false);
              loadAccess(u.id);
            }}
            className="p-2.5 text-sm hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
          >
            {u.shop_name || u.first_name} {u.shop_place || u.last_name} ({u.phone})
          </div>
        ))}
      {approvedUsers.filter(u => `${u.shop_name || u.first_name || ''} ${u.shop_place || u.last_name || ''} ${u.phone || ''}`
        .toLowerCase().includes(customerSearch.toLowerCase())).length === 0 && (
        <div className="p-2.5 text-sm text-gray-400">No matching customers</div>
      )}
    </div>
  )}
</div>
      </div>

      {selectedUser && (
        <>
          <p className="text-sm text-gray-600 mb-3">
            Managing: <strong>{selectedUser.shop_name || selectedUser.first_name}</strong> — {selectedUser.phone}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            {categories.map(cat => {
              const isBlocked = blockedCats.includes(cat.id);
              return (
                <div key={cat.id} onClick={() => toggleCategory(cat.id)}
                  className={`flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-colors ${isBlocked ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
                  <span className={`text-sm font-medium ${isBlocked ? 'text-red-700' : 'text-green-700'}`}>{cat.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isBlocked ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
                    {isBlocked ? 'BLOCKED' : 'VISIBLE'}
                  </span>
                </div>
              );
            })}
          </div>
          <button onClick={saveAccess} disabled={saving}
            className="px-4 py-2 bg-[#1A3626] text-white rounded-md text-sm font-semibold hover:bg-[#2a5440] disabled:opacity-50"
            data-testid="save-category-access">
            {saving ? 'Saving...' : 'Save Access Settings'}
          </button>
        </>
      )}
    </div>
  );
};

export default AdminPage;
